import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SEO from '../../components/SEO';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://trigreensoul.com';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "TRIGREENSOUL PRIVATE CUSTOM SERVICES, S.L.",
        "alternateName": "TRIGREENSOUL",
        "url": siteUrl,
        "logo": {
          "@type": "ImageObject",
          "url": "https://public.readdy.ai/ai/img_res/ae0a5222-1684-471a-9ad4-134e3508a5e0.png"
        },
        "description": "Leading European distributor of certified organic fruits and vegetables, committed to sustainability and excellence",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Barcelona",
          "addressRegion": "Catalonia",
          "addressCountry": "ES"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "Business Inquiries",
          "email": "info@trigreensoul.com",
          "availableLanguage": ["English", "Spanish"]
        },
        "areaServed": {
          "@type": "Place",
          "name": "Europe"
        },
        "knowsAbout": ["Organic Agriculture", "Sustainable Food Distribution", "Organic Crop Consulting"]
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "TRIGREENSOUL",
        "description": "Europe's leading distributor of certified organic fruits and vegetables",
        "publisher": {
          "@id": `${siteUrl}/#organization`
        },
        "inLanguage": "en"
      },
      {
        "@type": "WebPage",
        "@id": `${siteUrl}/#webpage`,
        "url": siteUrl,
        "name": "TRIGREENSOUL - Organic Agriculture, Naturally Responsible",
        "isPartOf": {
          "@id": `${siteUrl}/#website`
        },
        "about": {
          "@id": `${siteUrl}/#organization`
        },
        "description": "Leading European distributor of certified organic fruits and vegetables, committed to sustainability and excellence",
        "inLanguage": "en"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="TRIGREENSOUL - Organic Agriculture, Naturally Responsible | Barcelona Organic Fruits & Vegetables"
        description="TRIGREENSOUL is Europe's leading distributor of certified organic fruits and vegetables. Based in Barcelona, we provide premium organic produce, international trade services, and expert organic crop consulting to businesses across Europe and beyond."
        keywords="organic agriculture, organic fruits and vegetables, sustainable food distribution, organic crop consulting, European organic suppliers, Barcelona organic distributor, certified organic produce, B2B organic trade"
        canonical={siteUrl}
        schema={schema}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/ae0a5222-1684-471a-9ad4-134e3508a5e0.png" alt="TRIGREENSOUL" className="h-12 w-auto" />
            <span className={`text-xl font-semibold ${scrolled ? 'text-gray-900' : 'text-white'}`}>TRIGREENSOUL</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/about" className={`text-sm font-medium transition-colors hover:text-emerald-600 ${scrolled ? 'text-gray-700' : 'text-white'}`}>About</Link>
            <Link to="/products" className={`text-sm font-medium transition-colors hover:text-emerald-600 ${scrolled ? 'text-gray-700' : 'text-white'}`}>Products</Link>
            <Link to="/services" className={`text-sm font-medium transition-colors hover:text-emerald-600 ${scrolled ? 'text-gray-700' : 'text-white'}`}>Services</Link>
            <Link to="/sustainability" className={`text-sm font-medium transition-colors hover:text-emerald-600 ${scrolled ? 'text-gray-700' : 'text-white'}`}>Sustainability</Link>
            <Link to="/contact" className={`px-6 py-2 rounded-full border-2 transition-all whitespace-nowrap ${scrolled ? 'border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white' : 'border-white text-white hover:bg-white hover:text-emerald-700'}`}>Partner With Us</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/807751780ab3dbb4d376650e35795096.jpg" alt="Organic farmland" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight">
            Organic Agriculture,<br />
            <span className="text-emerald-400">Naturally Responsible</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto">
            Leading European distributor of certified organic fruits and vegetables, committed to sustainability and excellence
          </p>
          <Link to="/contact" className="inline-block px-10 py-4 bg-emerald-600 text-white text-lg font-semibold rounded-full hover:bg-emerald-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
            Explore Our Story →
          </Link>
        </div>
        <div className="absolute bottom-8 left-0 right-0 text-center">
          <p className="text-white text-lg font-bold tracking-widest opacity-85 animate-pulse">CERTIFIED ORGANIC EXCELLENCE</p>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm text-gray-500 uppercase tracking-wider">About TRIGREENSOUL</span>
              <h2 className="text-5xl font-bold text-gray-900 mt-4 mb-6 leading-tight">
                Cultivating Trust<br />Through Transparency
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                TRIGREENSOUL PRIVATE CUSTOM SERVICES, S.L. is a pioneering force in organic agriculture and sustainable food distribution. Based in Barcelona, we specialize in the retail trade of certified organic fruits and vegetables, serving discerning partners across Europe and beyond.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Our commitment extends beyond distribution—we provide expert organic crop advisory services, helping farmers and businesses transition to sustainable practices while maintaining the highest quality standards.
              </p>
              <Link to="/about" className="inline-block px-8 py-3 bg-emerald-800 text-white font-semibold rounded-full hover:bg-emerald-900 transition-all cursor-pointer whitespace-nowrap">
                Discover Our Values →
              </Link>
            </div>
            <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://readdy.ai/api/search-image?query=Professional%20hands%20carefully%20holding%20fresh%20organic%20vegetables%20including%20vibrant%20red%20tomatoes%20and%20green%20leafy%20produce%20against%20a%20clean%20minimalist%20background%20showcasing%20quality%20and%20care%20in%20sustainable%20agriculture%20with%20natural%20lighting%20and%20earth%20tones%20emphasizing%20freshness%20and%20organic%20certification&width=800&height=1000&seq=about001&orientation=portrait" alt="Organic produce" className="w-full h-full object-cover object-center" />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-amber-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <span className="text-sm text-gray-500 uppercase tracking-wider">WHY TRIGREENSOUL</span>
            <h2 className="text-5xl font-bold text-gray-900 mt-4 leading-tight">
              Sustainable Excellence<br />Delivered Globally
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-stone-100 rounded-2xl p-12 hover:shadow-xl transition-all">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-leaf-line text-4xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Certified Organic</h3>
              <p className="text-gray-600 leading-relaxed">
                Every product meets rigorous EU organic certification standards. We ensure complete traceability from farm to table, guaranteeing authenticity and quality in every delivery.
              </p>
            </div>
            <div className="bg-stone-100 rounded-2xl p-12 hover:shadow-xl transition-all">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-global-line text-4xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Global Distribution</h3>
              <p className="text-gray-600 leading-relaxed">
                Our extensive network spans across Europe and international markets, providing reliable supply chains and logistics expertise for partners seeking premium organic produce.
              </p>
            </div>
            <div className="bg-stone-100 rounded-2xl p-12 hover:shadow-xl transition-all">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-hand-heart-line text-4xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Expert Advisory</h3>
              <p className="text-gray-600 leading-relaxed">
                Our team of agronomists and sustainability consultants provides comprehensive guidance on organic crop management, certification processes, and market strategies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://readdy.ai/api/search-image?query=Modern%20warehouse%20distribution%20center%20with%20organized%20rows%20of%20fresh%20organic%20produce%20in%20wooden%20crates%20and%20boxes%20showing%20professional%20logistics%20and%20supply%20chain%20management%20with%20clean%20industrial%20setting%20and%20natural%20lighting%20emphasizing%20efficiency%20and%20quality%20control%20in%20food%20distribution&width=900&height=700&seq=service001&orientation=landscape" alt="Distribution center" className="w-full h-full object-cover object-center" />
            </div>
            <div>
              <span className="text-sm text-emerald-700 uppercase tracking-wider font-semibold">Our Services</span>
              <h2 className="text-5xl font-bold text-gray-900 mt-4 mb-6 leading-tight">
                Comprehensive Organic Solutions
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-truck-line text-2xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Distribution & Logistics</h3>
                    <p className="text-gray-600">
                      Seamless supply chain management ensuring fresh, organic produce reaches your business on time, every time.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-earth-line text-2xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">International Trade</h3>
                    <p className="text-gray-600">
                      Expert navigation of international markets, customs, and regulations for smooth cross-border organic trade.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-plant-line text-2xl text-emerald-700"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Organic Crop Consulting</h3>
                    <p className="text-gray-600">
                      Professional advisory services for farmers and businesses transitioning to or optimizing organic production.
                    </p>
                  </div>
                </div>
              </div>
              <Link to="/services" className="inline-block mt-8 px-8 py-3 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap">
                View All Services →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-900 to-emerald-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://readdy.ai/api/search-image?query=Close-up%20macro%20photography%20of%20fresh%20organic%20vegetables%20with%20water%20droplets%20on%20vibrant%20green%20leaves%20and%20red%20tomatoes%20against%20a%20soft%20neutral%20beige%20background%20showcasing%20natural%20freshness%20and%20premium%20quality%20in%20sustainable%20agriculture%20with%20shallow%20depth%20of%20field%20and%20natural%20lighting&width=800&height=600&seq=cta001&orientation=landscape" alt="Fresh organic produce" className="w-full h-full object-cover object-center" />
            </div>
            <div className="text-white">
              <span className="text-sm text-emerald-300 uppercase tracking-wider font-semibold">B2B PARTNERSHIPS</span>
              <h2 className="text-5xl font-bold mt-4 mb-6 leading-tight">
                Partner With<br />Europe's Leading<br />Organic Supplier
              </h2>
              <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                Join our network of trusted partners and gain access to premium certified organic produce, expert consultation, and reliable distribution services tailored to your business needs.
              </p>
              <Link to="/contact" className="inline-block px-10 py-4 bg-white text-emerald-900 font-bold rounded-full hover:bg-emerald-50 transition-all hover:scale-105 cursor-pointer whitespace-nowrap">
                Contact Our Team →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-20 pb-12 mx-8 mt-16 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div>
              <h3 className="text-4xl font-light mb-4">Stay Connected</h3>
              <p className="text-gray-400 mb-8">Subscribe to organic industry insights</p>
            </div>
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-6">Quick Links</h4>
              <div className="space-y-4">
                <Link to="/about" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">About Us</Link>
                <Link to="/products" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Products</Link>
                <Link to="/sustainability" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Certifications</Link>
                <Link to="/contact" className="block text-white hover:text-emerald-400 transition-colors cursor-pointer">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-6">Contact</h4>
              <p className="text-white mb-8">info@trigreensoul.com</p>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-4">Headquarters</h4>
              <p className="text-white text-sm leading-relaxed">
                Barcelona, Spain<br />
                European Union
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2025 TRIGREENSOUL. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-emerald-400 transition-colors cursor-pointer text-sm">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
